import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import {
  advanceAuctionShuffle,
  fetchTournamentById,
  fetchAuctionTournamentPlayers,
  fetchAuctionShuffle,
  fetchAuctionLiveState,
  fetchAuctionSettings,
  fetchAuctionTeamSetup,
  resolveAuctionLiveState,
  startAuctionShuffle,
  revealAuctionShufflePlayer,
  updateTournamentPlayerBattingStyle,
  updateAuctionLivePlayer,
} from '../../../services/tournamentService';
import {
  fetchMatchStreamEvents,
  getStreamingDashboard,
  takeoverStreamControl,
} from '../../../services/matchService';
import { getAuctionManagerPath } from '../../../utils/organization';
import { getAuctionManagerPath as getCommunityAuctionManagerPath } from '../../../utils/community';
import { getAuctionManagerPath as getClubAuctionManagerPath } from '../../../utils/club';
import { buildTeamSlug, normalizeAuctionTeams } from '../../../utils/teams';
import { battingStyles } from '../../../constants/playerOptions';
import PageShell from '../../../components/layout/PageShell';
import { copyTextToClipboard } from '../../../utils/clipboard';

// ✅ Custom breadcrumbs example:
// const crumbs = [
//   { label: 'Home', to: '/' },
//   { label: 'Community', to: `/community/${communityId}` },
//   { label: 'Match Manager', to: `/community/${communityId}/match-manager/${managerId}` },
//   { label: 'Manual Match Format' },
// ];
// <PageShell breadcrumbs={crumbs}>...</PageShell>
// <PageShell breadcrumbOverrides={{ tournamentId: tournament?.name || 'Tournament' }}>
const TabButton = ({ isActive, label, onClick }) => (
  <button
    type="button"
    className={`btn ${isActive ? 'btn-primary' : 'btn-outline-primary'} px-3`}
    onClick={onClick}
  >
    {label}
  </button>
);

const PlayerBadge = ({ player }) => {
  if (!player) return null;

  return (
    <div className="d-flex align-items-center gap-2">
      {player.profilePhoto ? (
        <img
          src={player.profilePhoto}
          alt={player.fullName || 'Player'}
          className="rounded-circle"
          width={40}
          height={40}
          style={{ objectFit: 'cover' }}
        />
      ) : (
        <div
          className="rounded-circle bg-light d-flex align-items-center justify-content-center"
          style={{ width: 40, height: 40 }}
        >
          <span className="fw-semibold text-uppercase">{player.fullName?.[0] || '?'}</span>
        </div>
      )}
      <div>
        <p className="mb-0 fw-semibold">{player.fullName || 'Unnamed player'}</p>
        <small className="text-muted">{player.primaryRole || 'Player'}</small>
      </div>
    </div>
  );
};

const LoadingState = ({ message }) => (
  <PageShell containerClassName="p-0">
    <div className="py-4">
      <p className="text-muted mb-0">{message}</p>
    </div>
  </PageShell>
);

const normalizeTeamKey = (teamName = '') =>
  teamName?.toString?.()?.trim?.()?.replace(/\s+/g, ' ')?.toLowerCase?.() || '';

const VALID_TABS = ['shuffle', 'teams', 'players'];
const AUTO_RESOLVE_BID_MS = 30000;
const getAuctionProcessMode = (value) => (value === 'manual' ? 'manual' : 'auto');
const severityBadgeClass = {
  info: 'text-bg-info',
  warning: 'text-bg-warning text-dark',
  critical: 'text-bg-danger',
};

const StartOnlineAuctionPage = () => {
  const { token, user, authReady: authHydrated, isLoadingAuth } = useAuth();
  const navigate = useNavigate();
  const { organizationId, communityId, clubId, auctionManagerId, tournamentId, tab } = useParams();
  const isIndividualRoute = Boolean(tournamentId && !organizationId && !communityId && !clubId);
  const auctionManagementPath =
    (isIndividualRoute
      ? `/individual-tournament/${tournamentId}/auction`
      : communityId
      ? getCommunityAuctionManagerPath(communityId, auctionManagerId, tournamentId)
      : clubId
        ? getClubAuctionManagerPath(clubId, auctionManagerId, tournamentId)
        : getAuctionManagerPath(organizationId, auctionManagerId, tournamentId)) || '/management/auction';
  const startAuctionPath = `${auctionManagementPath}/online/start`;
  const activeTab = VALID_TABS.includes(tab) ? tab : 'shuffle';
  const normalizeId = (value) => {
    if (!value) return '';
    if (typeof value === 'string') return value;
    if (value?._id) return String(value._id);
    if (value?.id) return String(value.id);
    return String(value);
  };
  const currentUserId = normalizeId(user?._id || user?.id || user?.profile?._id || user?.profile?.user?._id);
  const authReady = Boolean(authHydrated) && !isLoadingAuth && Boolean(token) && Boolean(currentUserId);
  const initializedRef = useRef(false);
  const [loadingTournament, setLoadingTournament] = useState(false);
  const [tournamentAccessLoaded, setTournamentAccessLoaded] = useState(false);
  const [auctionAllowed, setAuctionAllowed] = useState(true);

  const [shuffleState, setShuffleState] = useState(null);
  const [isLoadingShuffle, setIsLoadingShuffle] = useState(false);
  const [isRevealing, setIsRevealing] = useState(false);
  const [isAdvancing, setIsAdvancing] = useState(false);
  const [shuffleError, setShuffleError] = useState('');
  const [teamSetup, setTeamSetup] = useState(null);
  const [setupAllocations, setSetupAllocations] = useState([]);
  const [isLoadingTeams, setIsLoadingTeams] = useState(false);
  const [players, setPlayers] = useState([]);
  const [isLoadingPlayers, setIsLoadingPlayers] = useState(false);
  const [playersError, setPlayersError] = useState('');
  const [playerFilter, setPlayerFilter] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [minBid, setMinBid] = useState(0);
  const [maxBid, setMaxBid] = useState(100000);
  const [auctionSettings, setAuctionSettings] = useState(null);
  const [liveBid, setLiveBid] = useState(null);
  const [livePlayerSnapshot, setLivePlayerSnapshot] = useState(null);
  const [liveBidError, setLiveBidError] = useState('');
  const [manualSoldTeam, setManualSoldTeam] = useState('');
  const [manualSoldTeamIndex, setManualSoldTeamIndex] = useState('');
  const [manualSoldPrice, setManualSoldPrice] = useState('');
  const [autoResolveMs, setAutoResolveMs] = useState(null);
  const [isAutoResolving, setIsAutoResolving] = useState(false);
  const [battingStyleChoice, setBattingStyleChoice] = useState('');
  const [battingStyleError, setBattingStyleError] = useState('');
  const [battingStylePlayerId, setBattingStylePlayerId] = useState('');
  const [isUpdatingBattingStyle, setIsUpdatingBattingStyle] = useState(false);
  const [streamInfo, setStreamInfo] = useState(null);
  const [streamMessage, setStreamMessage] = useState('');
  const [logModal, setLogModal] = useState({ open: false, items: [] });
  const auctionProcessMode = getAuctionProcessMode(auctionSettings?.auctionProcess);
  const isManualProcess = auctionProcessMode === 'manual';

  const displayTeams = useMemo(() => normalizeAuctionTeams(teamSetup), [teamSetup]);
  const teamAllocationLookup = useMemo(() => {
    const map = new Map();

    const recordAllocation = (teamName, allocation, overwrite = false) => {
      if (!teamName || !allocation) return;

      const normalizedKey = normalizeTeamKey(teamName);
      const slugKey = allocation?.slug ? buildTeamSlug(allocation.slug) : buildTeamSlug(teamName);
      const totalAllocated = Number.isFinite(Number(allocation.totalAllocated))
        ? Number(allocation.totalAllocated)
        : 0;
      const soldPlayers = Number.isFinite(Number(allocation.soldPlayers))
        ? Number(allocation.soldPlayers)
        : 0;
      if (!normalizedKey) return;

      const existing = map.get(normalizedKey);
      if (existing && !overwrite) {
        map.set(normalizedKey, {
          totalAllocated: existing.totalAllocated + totalAllocated,
          soldPlayers: existing.soldPlayers + soldPlayers,
          slug: existing.slug || slugKey,
        });
        return;
      }

      map.set(normalizedKey, { totalAllocated, soldPlayers, slug: slugKey });
    };

    const aggregatedAllocations = [
      ...(Array.isArray(setupAllocations) ? setupAllocations : []),
      ...(Array.isArray(shuffleState?.teamAllocations) ? shuffleState.teamAllocations : []),
    ];

    aggregatedAllocations.forEach((allocation) => {
      recordAllocation(allocation.name || allocation.slug, allocation, true);
    });

    const hasAggregatedAllocations = aggregatedAllocations.length > 0;

    if (!hasAggregatedAllocations && Array.isArray(shuffleState?.order)) {
      shuffleState.order.forEach((entry) => {
        if (entry?.status !== 'sold') return;

        const price = Number(entry.soldPrice);
        if (!Number.isFinite(price)) return;

        recordAllocation(entry.soldToTeam, { totalAllocated: price, soldPlayers: 1 }, false);
      });
    }

    if (Array.isArray(teamSetup?.teams)) {
      teamSetup.teams.forEach((team, index) => {
        if (team.allocation) {
          recordAllocation(team.name || `Team ${index + 1}`, team.allocation, true);
        }
      });
    }

    return map;
  }, [setupAllocations, shuffleState?.order, shuffleState?.teamAllocations, teamSetup?.teams]);

  const currentPlayer = useMemo(() => shuffleState?.currentPlayer || null, [shuffleState]);
  const liveBidForCurrentPlayer = useMemo(() => {
    if (!currentPlayer?.player || !liveBid) return null;

    const playerId = currentPlayer.player.id || currentPlayer.player._id || '';
    const playerName = currentPlayer.player.fullName || '';

    if (liveBid.playerId) {
      return liveBid.playerId === playerId ? liveBid : null;
    }

    if (playerName && liveBid.playerName) {
      return liveBid.playerName === playerName ? liveBid : null;
    }

    return null;
  }, [currentPlayer, liveBid]);
  const needsManualSaleDetails = useMemo(() => {
    if (!liveBidForCurrentPlayer) return true;

    const hasTeam = Boolean(liveBidForCurrentPlayer.teamName?.trim?.());
    const bidAmount = Number(liveBidForCurrentPlayer.amount);
    const hasAmount = Number.isFinite(bidAmount) && bidAmount > 0;

    return !hasTeam || !hasAmount;
  }, [liveBidForCurrentPlayer]);
  const needsBattingStyle = useMemo(() => {
    const battingStyle =
      currentPlayer?.player?.skills?.battingStyle || currentPlayer?.player?.battingStyle || '';
    return Boolean(currentPlayer?.player && !battingStyle);
  }, [currentPlayer]);
  const lastPlayerIdRef = useRef(null);
  useEffect(() => {
    if (!currentPlayer?.player) {
      setManualSoldTeam('');
      setManualSoldTeamIndex('');
      setManualSoldPrice('');
      lastPlayerIdRef.current = null;
      return;
    }

    const playerId = currentPlayer.player.id || currentPlayer.player._id || '';
    const didPlayerChange = lastPlayerIdRef.current !== playerId;
    const bidTeam = liveBidForCurrentPlayer?.teamName?.trim?.() || '';
    const bidAmount = Number.isFinite(Number(liveBidForCurrentPlayer?.amount))
      ? String(liveBidForCurrentPlayer.amount)
      : '';

    if (didPlayerChange) {
      setManualSoldTeam(bidTeam);
      const bidTeamIndex = displayTeams.findIndex(
        (team) => normalizeTeamKey(team?.name) === normalizeTeamKey(bidTeam)
      );
      setManualSoldTeamIndex(bidTeamIndex >= 0 ? String(bidTeamIndex) : '');
      setManualSoldPrice(bidAmount);
      lastPlayerIdRef.current = playerId;
      return;
    }

    setManualSoldTeam((prev) => (prev ? prev : bidTeam));
    setManualSoldTeamIndex((prev) => {
      if (prev !== '') return prev;
      const bidTeamIndex = displayTeams.findIndex(
        (team) => normalizeTeamKey(team?.name) === normalizeTeamKey(bidTeam)
      );
      return bidTeamIndex >= 0 ? String(bidTeamIndex) : '';
    });
    setManualSoldPrice((prev) => (prev ? prev : bidAmount));
  }, [currentPlayer, displayTeams, liveBidForCurrentPlayer]);
  useEffect(() => {
    if (!currentPlayer?.player) {
      setBattingStyleChoice('');
      setBattingStyleError('');
      setBattingStylePlayerId('');
      return;
    }

    const playerId = currentPlayer.player.id || currentPlayer.player._id || '';
    const battingStyle =
      currentPlayer.player.skills?.battingStyle || currentPlayer.player.battingStyle || '';

    setBattingStylePlayerId(playerId);
    setBattingStyleChoice(battingStyle || '');
    setBattingStyleError('');
  }, [currentPlayer]);
  const hasPendingPlayers = useMemo(
    () =>
      typeof shuffleState?.hasPendingPlayers === 'boolean'
        ? shuffleState.hasPendingPlayers
        : (shuffleState?.order || []).some((entry) => entry.status === 'pending'),
    [shuffleState]
  );
  const resolvedPlayers = useMemo(() => (players || []).map((player) => player.player || player), [players]);

  const playersWithAuctionMeta = useMemo(() => {
    const orderEntries = shuffleState?.order || [];

    return resolvedPlayers.map((player) => {
      const playerId = player.id || player._id;
      const playerName = (player.fullName || player.name || player.personal?.fullName || '').toLowerCase();

      const matchingEntries = orderEntries.filter((entry) => {
        const entryId = entry.player?.id || entry.player?._id;
        const entryName = (entry.player?.fullName || '').toLowerCase();

        if (playerId && entryId) return playerId === entryId;
        return playerName && entryName && playerName === entryName;
      });

      const matchedEntry = matchingEntries.find((entry) => entry.status === 'sold') || matchingEntries.find((entry) => entry.status === 'pending') || matchingEntries[matchingEntries.length - 1];

      return {
        ...player,
        auctionStatus: matchedEntry?.status || 'available',
        soldPrice: matchedEntry?.soldPrice,
        soldToTeam: matchedEntry?.soldToTeam,
        attempts: matchedEntry?.attempts ?? 0,
      };
    });
  }, [resolvedPlayers, shuffleState]);

  const availableCategories = useMemo(
    () =>
      Array.from(
        new Set(
          playersWithAuctionMeta
            .map((player) => player.primaryRole || player.skills?.primaryRole)
            .filter(Boolean)
        )
      ),
    [playersWithAuctionMeta]
  );

  const filteredPlayers = useMemo(() => {
    return playersWithAuctionMeta.filter((player) => {
      const status = (player.auctionStatus || '').toLowerCase();
      const category = player.primaryRole || player.skills?.primaryRole || '';

      switch (playerFilter) {
        case 'available':
          return status === 'available' || status === 'pending';
        case 'sold':
          return status === 'sold';
        case 'unsold':
          return status === 'unsold' || status === 'disqualified';
        case 'category':
          return selectedCategory === 'all' ? true : category === selectedCategory;
        case 'reauction':
          return status === 'unsold';
        default:
          return true;
      }
    });
  }, [playerFilter, playersWithAuctionMeta, selectedCategory]);

  useEffect(() => {
    const checkIndividualAccess = async () => {
      if (!isIndividualRoute || !tournamentId) return;
      if (!authReady || !currentUserId) {
        setTournamentAccessLoaded(false);
        setLoadingTournament(true);
        return;
      }
      setLoadingTournament(true);
      try {
        const response = await fetchTournamentById(tournamentId, token);
        const tournament = response?.tournament || null;
        const individualAuctionAllowed =
          tournament?.ownerType === 'individual' &&
          (
            normalizeId(tournament?.ownerUser) === currentUserId ||
            normalizeId(tournament?.createdBy) === currentUserId ||
            normalizeId(tournament?.auctionManager) === currentUserId ||
            (tournament?.auctionManagers || []).some((id) => normalizeId(id) === currentUserId)
          );
        const existingOrgCommunityClubAuctionPermission = !isIndividualRoute;
        const allowed = individualAuctionAllowed || existingOrgCommunityClubAuctionPermission;
        setAuctionAllowed(allowed);
        setTournamentAccessLoaded(true);
      } catch (error) {
        setTournamentAccessLoaded(true);
        setAuctionAllowed(false);
      } finally {
        setLoadingTournament(false);
      }
    };
    checkIndividualAccess();
  }, [authReady, currentUserId, isIndividualRoute, token, tournamentId, user?._id]);

  useEffect(() => {
    if (tab && !VALID_TABS.includes(tab)) {
      navigate(`${startAuctionPath}/shuffle`, { replace: true });
    }
  }, [navigate, startAuctionPath, tab]);

  useEffect(() => {
    const loadShuffle = async () => {
      if (!authReady || !currentUserId || !token || !tournamentId) {
        return;
      }

      setIsLoadingShuffle(true);
      setShuffleError('');

      try {
        const response = await fetchAuctionShuffle(tournamentId, token);
        const resolvedShuffle =
          response?.resolved && ['sold', 'unsold'].includes(response.outcome)
            ? { ...response.shuffle, currentPlayer: null }
            : response.shuffle;
        setShuffleState(resolvedShuffle);
      } catch (error) {
        setShuffleState(null);
        setShuffleError(error.message || 'Unable to load shuffle right now.');
      } finally {
        setIsLoadingShuffle(false);
      }
    };

    loadShuffle();
  }, [authReady, currentUserId, tournamentId, token]);

  useEffect(() => {
    const loadPlayers = async () => {
      if (!authReady || !currentUserId || !token || !tournamentId) {
        return;
      }

      setIsLoadingPlayers(true);
      setPlayersError('');

      try {
        const response = await fetchAuctionTournamentPlayers(tournamentId, token);
        setPlayers(Array.isArray(response?.players) ? response.players : response || []);
      } catch (error) {
        setPlayers([]);
        setPlayersError(error.message || 'Unable to load players right now.');
      } finally {
        setIsLoadingPlayers(false);
      }
    };

    loadPlayers();
  }, [authReady, currentUserId, token, tournamentId]);

  const loadTeams = useCallback(async () => {
    if (!authReady || !currentUserId || !token || !tournamentId) {
      return;
    }

    setIsLoadingTeams(true);
    setSetupAllocations([]);

    try {
      const [setupResponse, settingsResponse] = await Promise.all([
        fetchAuctionTeamSetup(tournamentId, token),
        fetchAuctionSettings(tournamentId, token),
      ]);

      setTeamSetup(setupResponse.setup);
      setSetupAllocations(
        Array.isArray(setupResponse.allocations) ? setupResponse.allocations : []
      );
      setAuctionSettings(settingsResponse.settings || null);
    } catch (error) {
      setTeamSetup(null);
      setSetupAllocations([]);
      setAuctionSettings(null);
    } finally {
      setIsLoadingTeams(false);
    }
  }, [authReady, currentUserId, token, tournamentId]);


  const loadStreamInfo = useCallback(async () => {
    if (!authReady || !currentUserId || !token || !tournamentId) {
      return;
    }
    try {
      const payload = await getStreamingDashboard({ token, limit: 250, tournamentId });
      const streamRow = (payload?.items || []).find(
        (row) => row.streamType === 'online_auction' && String(row.tournamentId || '') === String(tournamentId)
      );
      setStreamInfo(streamRow || null);
    } catch (error) {
      setStreamInfo(null);
    }
  }, [authReady, currentUserId, token, tournamentId]);


  useEffect(() => {
    const basePrice = Number(currentPlayer?.player?.basePrice ?? currentPlayer?.player?.minBid ?? 0);
    const providedMax = Number(currentPlayer?.player?.maxBid ?? 0);
    const tentativeMax = providedMax || (basePrice ? basePrice * 10 : 100000);
    const safeMax = tentativeMax > basePrice ? tentativeMax : basePrice + 1000;

    if (Number.isFinite(basePrice)) {
      setMinBid(Math.max(basePrice, 0));
    }

    if (Number.isFinite(safeMax)) {
      setMaxBid(safeMax || 100000);
    }

    if (!currentPlayer) {
      setMinBid(0);
      setMaxBid(100000);
    }
  }, [currentPlayer]);

  useEffect(() => {
    if (!authReady || !currentUserId || !token || !tournamentId) {
      return;
    }

    const syncLivePlayer = async () => {
      const payload = currentPlayer?.player
        ? {
            playerId: currentPlayer.player.id || currentPlayer.player._id,
            playerName: currentPlayer.player.fullName || 'Unnamed player',
            role: currentPlayer.player.primaryRole || currentPlayer.player.skills?.primaryRole || 'Player',
            basePrice: Number(currentPlayer.player.basePrice ?? currentPlayer.player.minBid ?? 0),
            minBid,
            maxBid,
            attempts: currentPlayer.attempts ?? 0,
            updatedAt: new Date().toISOString(),
          }
        : null;

      try {
        await updateAuctionLivePlayer(tournamentId, payload, token);
      } catch (error) {
        // Ignore live update errors; auction manager can retry on next change.
      }
    };

    syncLivePlayer();
  }, [authReady, currentUserId, currentPlayer, maxBid, minBid, token, tournamentId]);

  useEffect(() => {
    if (!authReady || !currentUserId || !token || !tournamentId) {
      return;
    }

    let isMounted = true;

    const loadLiveState = async () => {
      try {
        setLiveBidError('');
        const response = await fetchAuctionLiveState(tournamentId, token);
        if (!isMounted) return;
        setLiveBid(response.live?.latestBid || null);
        setLivePlayerSnapshot(response.live?.currentPlayer || null);
        setAuctionSettings((previous) => {
          const nextProcess = getAuctionProcessMode(response.live?.auctionProcess);
          if (previous?.auctionProcess === nextProcess) return previous;
          return { ...(previous || {}), auctionProcess: nextProcess };
        });
      } catch (error) {
        if (!isMounted) return;
        setLiveBid(null);
        setLivePlayerSnapshot(null);
        setLiveBidError(error.message || 'Unable to load the latest live bid.');
      }
    };

    loadLiveState();
    const intervalId = setInterval(loadLiveState, 4000);

    return () => {
      isMounted = false;
      clearInterval(intervalId);
    };
  }, [authReady, currentUserId, token, tournamentId]);

  useEffect(() => {
    if (!currentPlayer || isManualProcess) {
      setAutoResolveMs(null);
      setIsAutoResolving(false);
      return;
    }

    const getLastActivityTime = () => {
      if (liveBidForCurrentPlayer?.createdAt) {
        return new Date(liveBidForCurrentPlayer.createdAt).getTime();
      }

      if (livePlayerSnapshot?.updatedAt) {
        return new Date(livePlayerSnapshot.updatedAt).getTime();
      }

      return null;
    };

    const updateRemaining = () => {
      const lastActivity = getLastActivityTime();
      if (!lastActivity) {
        setAutoResolveMs(null);
        return;
      }

      const elapsedMs = Date.now() - lastActivity;
      const remaining = Math.max(AUTO_RESOLVE_BID_MS - elapsedMs, 0);
      setAutoResolveMs(remaining);
    };

    updateRemaining();
    const intervalId = setInterval(updateRemaining, 1000);

    return () => clearInterval(intervalId);
  }, [currentPlayer, isManualProcess, liveBidForCurrentPlayer, livePlayerSnapshot]);

  useEffect(() => {
    if (!authReady || !currentUserId || !token || !tournamentId) {
      return;
    }
    if (isManualProcess) return;
    if (!currentPlayer || autoResolveMs === null || autoResolveMs > 0 || isAutoResolving) return;

    let isActive = true;

    const resolveNow = async () => {
      setIsAutoResolving(true);
      setShuffleError('');

      try {
        const response = await resolveAuctionLiveState(tournamentId, token);
        if (!isActive || !response?.resolved) return;

        const resolvedShuffle =
          response?.resolved && ['sold', 'unsold'].includes(response.outcome)
            ? { ...response.shuffle, currentPlayer: null }
            : response.shuffle;
        setShuffleState(resolvedShuffle);
        setLiveBid(null);
        setLivePlayerSnapshot(null);
        if (response.outcome === 'sold') {
          await loadTeams();
        }
      } catch (error) {
        if (!isActive) return;
        setShuffleError(error.message || 'Unable to auto-resolve live bid.');
      } finally {
        if (isActive) {
          setIsAutoResolving(false);
        }
      }
    };

    resolveNow();

    return () => {
      isActive = false;
    };
  }, [authReady, currentUserId, autoResolveMs, currentPlayer, isAutoResolving, isManualProcess, loadTeams, token, tournamentId]);

  useEffect(() => {
    if (!authReady || !currentUserId || !tournamentId) return;
    if (initializedRef.current) return;
    initializedRef.current = true;
    loadTeams();
    loadStreamInfo();
  }, [authReady, currentUserId, loadStreamInfo, loadTeams, tournamentId]);

  const handleShowNewPlayer = async () => {
    if (!token || !tournamentId) return;

    setIsRevealing(true);
    setShuffleError('');

    try {
      let activeShuffle = shuffleState;

      if (!activeShuffle) {
        const startResponse = await startAuctionShuffle(tournamentId, token);
        activeShuffle = startResponse.shuffle;
        setShuffleState(activeShuffle);
      }

      if (activeShuffle?.currentPlayer) return;

      const response = await revealAuctionShufflePlayer(tournamentId, token);
      setShuffleState(response.shuffle);
    } catch (error) {
      setShuffleError(error.message || 'Unable to show the next player.');
    } finally {
      setIsRevealing(false);
    }
  };

  const handleOutcome = async (outcome) => {
    if (!token || !tournamentId) return;

    setShuffleError('');

    const saleDetails = {};

    if (outcome === 'sold') {
      const liveBidAmount = Number(liveBidForCurrentPlayer?.amount);
      const hasLiveWinningBid =
        Boolean(liveBidForCurrentPlayer?.teamName?.trim?.()) &&
        Number.isFinite(liveBidAmount) &&
        liveBidAmount > 0;
      const trimmedTeam = manualSoldTeam.trim();
      const parsedPrice = manualSoldPrice === '' ? null : Number(manualSoldPrice);
      const hasManualTeam = Boolean(trimmedTeam);
      const hasManualPrice = Number.isFinite(parsedPrice) && parsedPrice > 0;

      if (hasLiveWinningBid) {
        saleDetails.soldPrice = liveBidAmount;
        saleDetails.soldToTeamName = liveBidForCurrentPlayer.teamName;
        const bidTeamIndex = displayTeams.findIndex(
          (team) => normalizeTeamKey(team?.name) === normalizeTeamKey(liveBidForCurrentPlayer.teamName)
        );
        if (bidTeamIndex >= 0) {
          saleDetails.soldToTeamIndex = bidTeamIndex;
        }
      } else if (needsManualSaleDetails || hasManualTeam || hasManualPrice || !liveBidForCurrentPlayer) {
        if (!trimmedTeam) {
          setShuffleError('Select a team before marking the player as sold.');
          return;
        }

        if (!Number.isFinite(parsedPrice) || parsedPrice <= 0) {
          setShuffleError('Enter a valid sale price before marking the player as sold.');
          return;
        }

        saleDetails.soldToTeamName = trimmedTeam;
        saleDetails.soldPrice = parsedPrice;
        if (manualSoldTeamIndex !== '') {
          saleDetails.soldToTeamIndex = Number(manualSoldTeamIndex);
        }
      }
    }

    setIsAdvancing(true);

    try {
      const response = await advanceAuctionShuffle(tournamentId, outcome, token, saleDetails);
      setShuffleState(response.shuffle);
      setLiveBid(null);
      if (outcome === 'sold') {
        await loadTeams();
      }
    } catch (error) {
      setShuffleError(error.message || 'Unable to update shuffle.');
    } finally {
      setIsAdvancing(false);
    }
  };

  const handleBattingStyleUpdate = async () => {
    if (!token || !tournamentId || !battingStylePlayerId) return;

    if (!battingStyleChoice) {
      setBattingStyleError('Select a batting style before saving.');
      return;
    }

    setIsUpdatingBattingStyle(true);
    setBattingStyleError('');

    try {
      const response = await updateTournamentPlayerBattingStyle(
        tournamentId,
        battingStylePlayerId,
        battingStyleChoice,
        token
      );
      const updatedStyle =
        response?.player?.skills?.battingStyle ||
        response?.player?.battingStyle ||
        battingStyleChoice;

      setShuffleState((prev) => {
        if (!prev?.currentPlayer?.player) return prev;
        const currentId = prev.currentPlayer.player.id || prev.currentPlayer.player._id || '';
        if (currentId !== battingStylePlayerId) return prev;

        return {
          ...prev,
          currentPlayer: {
            ...prev.currentPlayer,
            player: {
              ...prev.currentPlayer.player,
              battingStyle: updatedStyle,
              skills: {
                ...(prev.currentPlayer.player.skills || {}),
                battingStyle: updatedStyle,
              },
            },
          },
        };
      });
      setPlayers((prev) =>
        prev.map((player) => {
          const playerId = player.id || player._id || '';
          if (playerId !== battingStylePlayerId) return player;
          return {
            ...player,
            battingStyle: updatedStyle,
            skills: {
              ...(player.skills || {}),
              battingStyle: updatedStyle,
            },
          };
        })
      );
    } catch (error) {
      setBattingStyleError(error.message || 'Unable to update batting style.');
    } finally {
      setIsUpdatingBattingStyle(false);
    }
  };

  const handleCopy = async (value, label) => {
    if (!value) return;
    try {
      await copyTextToClipboard(value);
      setStreamMessage(`${label} copied to clipboard.`);
    } catch (error) {
      setStreamMessage(error.message || `Unable to copy ${label.toLowerCase()}.`);
    }
  };

  const handleViewLogs = async () => {
    try {
      const payload = await fetchMatchStreamEvents({ token, tournamentId, streamType: 'online_auction' });
      setLogModal({ open: true, items: payload?.items || [] });
    } catch (error) {
      setStreamMessage(error.message || 'Unable to load stream logs.');
    }
  };

  const handleTakeover = async () => {
    try {
      await takeoverStreamControl({ token, tournamentId, streamType: 'online_auction' });
      setStreamMessage('Stream control takeover completed.');
      await loadStreamInfo();
    } catch (error) {
      setStreamMessage(error.message || 'Takeover failed.');
    }
  };

  const renderShuffleContent = () => {

    if (shuffleError) {
      return <div className="alert alert-danger mb-0">{shuffleError}</div>;
    }

    if (isLoadingShuffle) {
      return <p className="text-muted mb-0">Loading shuffle details...</p>;
    }

    const isBiddingCompleted = !currentPlayer && !(shuffleState?.canRevealNextPlayer ?? hasPendingPlayers) && Boolean(shuffleState);

    return (
      <div className="d-flex flex-column gap-3">
        {isBiddingCompleted ? (
          <div className="alert alert-success mb-0">
            <strong>Bidding completed.</strong> All players have completed the bidding process.
          </div>
        ) : null}
        {!currentPlayer && ((shuffleState?.canRevealNextPlayer ?? hasPendingPlayers) || !shuffleState) && (
          <button
            type="button"
            className="btn btn-primary align-self-start"
            onClick={handleShowNewPlayer}
            disabled={isRevealing || isLoadingShuffle}
          >
            Show new player
          </button>
        )}
        {currentPlayer ? (
          <div className="d-flex flex-column gap-3">
            <PlayerBadge player={currentPlayer.player} />
            {needsBattingStyle ? (
              <div className="border rounded p-3 bg-light d-grid gap-2">
                <div>
                  <p className="mb-1 fw-semibold">Batting style needed</p>
                  <p className="text-muted small mb-0">
                    This player&apos;s profile is missing a batting style. Please update it before continuing.
                  </p>
                </div>
                {battingStyleError ? (
                  <div className="alert alert-warning mb-0">{battingStyleError}</div>
                ) : null}
                <div className="row g-2 align-items-end">
                  <div className="col-12 col-md-6">
                    <label className="form-label small text-muted" htmlFor="batting-style-select">
                      Batting style
                    </label>
                    <select
                      id="batting-style-select"
                      className="form-select"
                      value={battingStyleChoice}
                      onChange={(event) => setBattingStyleChoice(event.target.value)}
                    >
                      <option value="">Select batting style</option>
                      {battingStyles.map((style) => (
                        <option key={style} value={style}>
                          {style}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-12 col-md-6">
                    <button
                      type="button"
                      className="btn btn-primary w-100"
                      onClick={handleBattingStyleUpdate}
                      disabled={isUpdatingBattingStyle}
                    >
                      {isUpdatingBattingStyle ? 'Saving...' : 'Update profile'}
                    </button>
                  </div>
                </div>
              </div>
            ) : null}
            {liveBidError ? <div className="alert alert-warning mb-0">{liveBidError}</div> : null}
            {liveBidForCurrentPlayer ? (
              <div className="border rounded p-3 bg-light d-flex flex-column gap-2">
                <div className="d-flex justify-content-between align-items-center">
                  <span className="text-muted">Latest bid</span>
                  <span className="fw-semibold">
                    ₹{Number(liveBidForCurrentPlayer.amount || 0).toLocaleString()}
                  </span>
                </div>
                <div className="d-flex flex-wrap justify-content-between text-muted small">
                  <span>{liveBidForCurrentPlayer.teamName || 'Team not set'}</span>
                  <span>
                    {liveBidForCurrentPlayer.owner?.name ||
                      liveBidForCurrentPlayer.owner?.email ||
                      'Team owner'}
                  </span>
                </div>
              </div>
            ) : (
              <div className="border rounded p-3 bg-light">
                <p className="text-muted mb-0">Waiting for bids from team owners.</p>
              </div>
            )}
            {!isManualProcess && autoResolveMs !== null ? (
              <div className="d-flex align-items-center justify-content-between text-muted small">
                <span>Auto-resolve timer</span>
                <span>{Math.max(Math.ceil(autoResolveMs / 1000), 0)}s</span>
              </div>
            ) : null}
            <div className="d-flex gap-2 flex-wrap">
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => handleOutcome('unsold')}
                disabled={isAdvancing || isAutoResolving}
              >
                Mark as unsold
              </button>
              <button
                type="button"
                className="btn btn-success"
                onClick={() => handleOutcome('sold')}
                disabled={isAdvancing || isAutoResolving}
              >
                Mark as sold
              </button>
            </div>
            {needsManualSaleDetails ? (
              <div className="border rounded p-3 bg-light d-grid gap-2">
                <p className="mb-0 text-muted">
                  {liveBidForCurrentPlayer
                    ? 'Manual sale details (live bid is missing team or price).'
                    : 'Manual sale details (no live bid found).'}
                </p>
                <div className="row g-2">
                  <div className="col-12 col-md-6">
                    <label className="form-label small text-muted" htmlFor="manual-sold-team">
                      Team
                    </label>
                    <select
                      id="manual-sold-team"
                      className="form-select"
                      value={manualSoldTeamIndex}
                      onChange={(event) => {
                        const selectedIndex = event.target.value;
                        const selectedTeam =
                          selectedIndex === '' ? '' : displayTeams[Number(selectedIndex)]?.name || '';
                        setManualSoldTeam(selectedTeam);
                        setManualSoldTeamIndex(selectedIndex);
                      }}
                      disabled={!displayTeams.length}
                    >
                      <option value="">Select team</option>
                      {displayTeams.map((team, index) => (
                        <option key={team._id || team.id || `${team.name}-${index}`} value={String(index)}>
                          {team.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-12 col-md-6">
                    <label className="form-label small text-muted" htmlFor="manual-sold-price">
                      Sold price
                    </label>
                    <input
                      id="manual-sold-price"
                      type="number"
                      className="form-control"
                      min="0"
                      value={manualSoldPrice}
                      onChange={(event) => setManualSoldPrice(event.target.value)}
                    />
                  </div>
                </div>
                {!displayTeams.length ? (
                  <small className="text-muted">Set up teams to record a manual sale.</small>
                ) : null}
              </div>
            ) : null}
          </div>
        ) : hasPendingPlayers ? null : (
          <p className="text-muted mb-0">No pending players. Shuffle complete.</p>
        )}
      </div>
    );
  };

  const renderTeams = () => {
    if (isLoadingTeams) {
      return <p className="text-muted mb-0">Loading teams...</p>;
    }

    if (!displayTeams.length) {
      return (
        <div className="d-flex flex-column gap-2">
          <p className="mb-0 text-muted">No teams configured for this auction yet.</p>
          <Link to={`${auctionManagementPath}/teams`} className="btn btn-outline-primary btn-sm align-self-start">
            Set up teams
          </Link>
        </div>
      );
    }

    return (
      <div className="row g-3">
        {displayTeams.map((team, index) => {
          const findPlayerById = (playerId) => {
            if (!playerId) return null;
            return (
              team.players?.find((player) => (player.id || player._id) === playerId) ||
              resolvedPlayers.find((player) => (player.id || player._id) === playerId)
            );
          };

          const getPlayerName = (player, fallbackId) =>
            player?.name ||
            player?.fullName ||
            player?.personal?.fullName ||
            player?.email ||
            player?.user?.name ||
            fallbackId ||
            'Not selected';

          const getPlayerImage = (player) => player?.image || player?.profilePhoto || player?.media?.profilePhoto;

          const captain = findPlayerById(team.captain);
          const owner = findPlayerById(team.ownerId);
          const initial = team.name?.charAt(0)?.toUpperCase?.() || 'T';
          const allocationKey = normalizeTeamKey(team.name || `Team ${index + 1}`);
          const slugKey = buildTeamSlug(team.name || `Team ${index + 1}`, index);
          const allocation =
            teamAllocationLookup.get(allocationKey) ||
            Array.from(teamAllocationLookup.values()).find((entry) => entry.slug === slugKey) ||
            null;
          const normalizedTeamName = normalizeTeamKey(team.name || `Team ${index + 1}`);
          const selectedPlayerIds = new Set(
            (team.players || [])
              .map((player) => player?.id || player?._id)
              .filter(Boolean),
          );
          let unknownSoldCount = 0;
          const soldEntries = (shuffleState?.order || []).filter((entry) => {
            const soldTeam = normalizeTeamKey(entry.soldToTeam || '');
            return entry.status === 'sold' && soldTeam === normalizedTeamName;
          });
          soldEntries.forEach((entry) => {
            const playerId = entry.player?.id || entry.player?._id;
            if (playerId) {
              selectedPlayerIds.add(playerId);
            } else {
              unknownSoldCount += 1;
            }
          });
          const totalSelectedFromOrder = selectedPlayerIds.size + unknownSoldCount;
          const allocatedSoldPlayers = allocation?.soldPlayers ?? 0;
          const baseSelectedCount = (team.players?.length || 0) + allocatedSoldPlayers;
          const playersSelected = Math.max(totalSelectedFromOrder, baseSelectedCount);
          const pursePerTeam = Number(auctionSettings?.pursePerTeam) || 0;
          const rosterLimit =
            Number(teamSetup?.playersPerTeam) || Number(auctionSettings?.playersPerTeam) || Number(team.playerCount) || 0;
          const baseBid = Number(auctionSettings?.baseBid) || 0;

          const fallbackAllocated = (shuffleState?.order || []).reduce((sum, entry) => {
            const soldTeam = normalizeTeamKey(entry.soldToTeam || '');
            const price = Number(entry.soldPrice);
            if (entry.status === 'sold' && soldTeam && soldTeam === normalizedTeamName && Number.isFinite(price)) {
              return sum + price;
            }
            return sum;
          }, 0);

          const allocationTotal = Number.isFinite(allocation?.totalAllocated)
            ? allocation.totalAllocated
            : 0;
          const totalAllocated = Math.max(allocationTotal, fallbackAllocated);

          const remainingBalance = Math.max(pursePerTeam - totalAllocated, 0);
          const remainingSlots = Math.max(rosterLimit - playersSelected, 0);
          const reservedForRemaining = Math.max(remainingSlots - 1, 0) * Math.max(baseBid, 0);
          const maxNextBid =
            remainingSlots > 0 ? Math.max(remainingBalance - reservedForRemaining, 0) : 0;

          const formatCurrency = (value) =>
            Number.isFinite(value) ? `₹${Number(value).toLocaleString()}` : '—';

          return (
            <div className="col-12 col-md-6 col-xl-4" key={`${team.name}-${index}`}>
              <Link
                to={`${auctionManagementPath}/online/start/teams/${buildTeamSlug(team.name, index)}`}
                className="card h-100 border-0 shadow-sm text-decoration-none text-reset"
              >
                <div className="card-body d-grid gap-3">
                  <div className="d-flex align-items-center gap-3">
                    {team.logoUrl ? (
                      <img
                        src={team.logoUrl}
                        alt={team.name}
                        className="rounded"
                        style={{ width: '64px', height: '64px', objectFit: 'cover' }}
                      />
                    ) : (
                      <div
                        className="rounded bg-secondary-subtle text-secondary-emphasis d-flex align-items-center justify-content-center"
                        style={{ width: '64px', height: '64px' }}
                        aria-hidden="true"
                      >
                        <span className="fw-bold">{initial}</span>
                      </div>
                    )}
                    <div>
                      <p className="fw-semibold mb-0">{team.name || `Team ${index + 1}`}</p>
                      <p className="text-muted mb-0 small">{playersSelected} players selected</p>
                    </div>
                  </div>

                  <div className="d-grid gap-2">
                    <div className="d-flex justify-content-between align-items-center">
                      <span className="text-muted">Total allocated amount</span>
                      <span className="fw-semibold">{formatCurrency(totalAllocated)}</span>
                    </div>
                    <div className="d-flex justify-content-between align-items-center">
                      <span className="text-muted">Remaining balance</span>
                      <span className="fw-semibold">{formatCurrency(remainingBalance)}</span>
                    </div>
                    <div className="d-flex justify-content-between align-items-center">
                      <span className="text-muted">Max bid for next player</span>
                      <span className="fw-semibold">{formatCurrency(maxNextBid)}</span>
                    </div>
                  </div>

                  <div className="border rounded p-3 bg-light d-flex align-items-center gap-3">
                    <div>
                      <p className="text-muted small mb-0">Owner</p>
                      <p className="fw-semibold mb-0">{getPlayerName(owner, 'Not selected')}</p>
                    </div>
                  </div>

                  <div className="border rounded p-3 bg-light d-flex align-items-center gap-3">
                    {getPlayerImage(captain) ? (
                      <img
                        src={getPlayerImage(captain)}
                        alt={getPlayerName(captain)}
                        className="rounded-circle"
                        width={48}
                        height={48}
                        style={{ objectFit: 'cover' }}
                      />
                    ) : (
                      <div
                        className="rounded-circle bg-secondary-subtle text-secondary-emphasis d-flex align-items-center justify-content-center"
                        style={{ width: '48px', height: '48px' }}
                        aria-hidden="true"
                      >
                        <span className="fw-semibold">{getPlayerName(captain)[0]?.toUpperCase?.() || 'C'}</span>
                      </div>
                    )}
                    <div>
                      <p className="text-muted small mb-0">Captain</p>
                      <p className="fw-semibold mb-0">{getPlayerName(captain)}</p>
                    </div>
                  </div>
                </div>
              </Link>
            </div>
          );
        })}
      </div>
    );
  };

  const renderPlayers = () => {
    const getStatusClass = (status) => {
      switch (status) {
        case 'sold':
          return 'text-bg-success';
        case 'unsold':
        case 'disqualified':
          return 'text-bg-warning text-dark';
        case 'pending':
        case 'available':
          return 'text-bg-secondary';
        default:
          return 'text-bg-light border';
      }
    };

    const renderSkills = (player) => (
      <div className="d-flex flex-wrap gap-2 mt-2">
        {player.skills?.battingStyle && (
          <span className="badge text-bg-secondary rounded-pill">{player.skills.battingStyle}</span>
        )}
        {player.skills?.bowlingStyle && (
          <span className="badge text-bg-success rounded-pill">{player.skills.bowlingStyle}</span>
        )}
        {player.skills?.strengths && (
          <span className="badge text-bg-light text-body rounded-pill">{player.skills.strengths}</span>
        )}
      </div>
    );

    const renderPlayerCard = (player) => {
      const name = player.fullName || player.name || player.personal?.fullName || 'Unnamed player';
      const role = player.primaryRole || player.skills?.primaryRole || 'Role not set';
      const avatar =
        player.profilePhoto || player.media?.profilePhoto || player.image || player.photoUrl || '/avatar.png';
      const status = (player.auctionStatus || 'available').toLowerCase();
      const soldLabel =
        player.soldToTeam && player.soldPrice
          ? `${player.soldToTeam} · ₹${Number(player.soldPrice).toLocaleString()}`
          : player.soldToTeam || '';

      return (
        <div className="col" key={player.id || player._id || name}>
          <div className="card h-100 shadow-sm">
            <div className="card-body d-flex flex-column gap-2">
              <div className="d-flex align-items-start gap-3">
                <div
                  className="rounded-circle bg-light border overflow-hidden flex-shrink-0"
                  style={{ width: '64px', height: '64px' }}
                >
                  <img src={avatar} alt={name} className="w-100 h-100 object-fit-cover" />
                </div>
                <div className="flex-grow-1 d-flex justify-content-between align-items-start gap-2">
                  <div>
                    <h3 className="h6 mb-1">{name}</h3>
                    <p className="text-muted small mb-1">{role}</p>
                    <span className={`badge ${getStatusClass(status)}`}>
                      {status === 'pending' ? 'Available' : status}
                    </span>
                    {soldLabel && <p className="small text-muted mb-0 mt-1">{soldLabel}</p>}
                  </div>
                  {player.attempts ? (
                    <span className="badge text-bg-light border flex-shrink-0">{player.attempts} attempts</span>
                  ) : null}
                </div>
              </div>
              {renderSkills(player)}
            </div>
          </div>
        </div>
      );
    };

    const isBiddingCompleted = !currentPlayer && !(shuffleState?.canRevealNextPlayer ?? hasPendingPlayers) && Boolean(shuffleState);

    return (
      <div className="d-flex flex-column gap-3">
        {isBiddingCompleted ? (
          <div className="alert alert-success mb-0">
            <strong>Bidding completed.</strong> All players have completed the bidding process.
          </div>
        ) : null}
        <div className="card">
          <div className="card-body d-flex flex-column gap-3">
            <div className="d-flex flex-wrap justify-content-between gap-3 align-items-start">
              <div>
                <p className="eyebrow text-uppercase text-primary fw-semibold mb-1 small">Players</p>
                <h3 className="h5 mb-1">Auction board</h3>
                <p className="text-muted mb-0">Track who is available, sold, or ready for re-auction.</p>
              </div>
              <div className="d-flex flex-wrap gap-2 ms-auto">
                <TabButton isActive={playerFilter === 'all'} label="All players" onClick={() => setPlayerFilter('all')} />
                <TabButton
                  isActive={playerFilter === 'available'}
                  label="Available"
                  onClick={() => setPlayerFilter('available')}
                />
                <TabButton isActive={playerFilter === 'sold'} label="Sold" onClick={() => setPlayerFilter('sold')} />
                <TabButton
                  isActive={playerFilter === 'unsold'}
                  label="Unsold"
                  onClick={() => setPlayerFilter('unsold')}
                />
                <TabButton
                  isActive={playerFilter === 'category'}
                  label="Category"
                  onClick={() => setPlayerFilter('category')}
                />
                <TabButton
                  isActive={playerFilter === 'reauction'}
                  label="Reauction of unsold"
                  onClick={() => setPlayerFilter('reauction')}
                />
              </div>
            </div>

            {playerFilter === 'category' && (
              <div className="d-flex flex-wrap gap-2 align-items-center">
                <label className="form-label mb-0">Filter by category</label>
                <select
                  className="form-select"
                  style={{ maxWidth: '240px' }}
                  value={selectedCategory}
                  onChange={(event) => setSelectedCategory(event.target.value)}
                >
                  <option value="all">All categories</option>
                  {availableCategories.map((category) => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {playersError && <div className="alert alert-danger mb-0">{playersError}</div>}
            {isLoadingPlayers ? (
              <p className="text-muted mb-0">Loading players...</p>
            ) : filteredPlayers.length ? (
              <div className="row row-cols-1 row-cols-md-2 row-cols-lg-3 g-3">
                {filteredPlayers.map((player) => renderPlayerCard(player))}
              </div>
            ) : (
              <p className="text-muted mb-0">No players found for this filter.</p>
            )}
          </div>
        </div>
      </div>
    );
  };

  const watchLiveUrl = auctionSettings?.livePlaybackUrl || auctionSettings?.youtubePlaybackUrl || auctionSettings?.youtubeLiveLink || auctionSettings?.streamUrl || '';
  const replayUrl = auctionSettings?.replay?.url || '';
  const publicAuctionUrl = `${window.location.origin}/public/tournaments/${tournamentId}/online-auction`;
  const dashboardWatchUrl = streamInfo?.watchLink || watchLiveUrl;
  const dashboardReplayUrl = streamInfo?.replay?.url || replayUrl;
  const hasTakeoverAction = (streamInfo?.alerts || []).some((alert) => alert.code === 'heartbeat_expired');

  if (!authReady || loadingTournament || (isIndividualRoute && !tournamentAccessLoaded)) {
    return <LoadingState message="Loading auction access..." />;
  }

  if (isIndividualRoute && tournamentAccessLoaded && !auctionAllowed) {
    return <LoadingState message="You are not authorized to access this auction." />;
  }

  return (
    <PageShell containerClassName="p-0">
    <div className="py-4 d-flex flex-column gap-3 align-items-start w-100">
      <div className="d-flex flex-wrap align-items-start gap-3 w-100">
        <div className="flex-grow-1">
          <p className="eyebrow text-uppercase text-primary fw-semibold mb-0">Online auction</p>
          <h1 className="h3 mb-1">Start auction</h1>
          <p className="text-muted mb-0">
            Prepare to launch the live online auction experience. Use the shuffle to pull players into bidding order
            and track outcomes.
          </p>
        </div>
        <div className="d-flex flex-wrap gap-2 ms-auto">
          <TabButton
            isActive={activeTab === 'shuffle'}
            label="Shuffle"
            onClick={() => navigate(`${startAuctionPath}/shuffle`)}
          />
          <TabButton
            isActive={activeTab === 'teams'}
            label="Teams"
            onClick={() => navigate(`${startAuctionPath}/teams`)}
          />
          <TabButton
            isActive={activeTab === 'players'}
            label="Players"
            onClick={() => navigate(`${startAuctionPath}/players`)}
          />
        </div>
      </div>

      {streamMessage ? <div className="alert alert-info py-2 w-100 mb-0">{streamMessage}</div> : null}

      <div className="card w-100">
        <div className="card-body d-flex flex-column gap-2">
          <div className="d-flex flex-wrap gap-2 align-items-center">
            <strong>Streaming:</strong>
            {dashboardWatchUrl ? <a className="btn btn-sm btn-outline-danger" href={dashboardWatchUrl} target="_blank" rel="noreferrer">Watch live</a> : <span className="text-muted small">Not live</span>}
            {dashboardReplayUrl ? <a className="btn btn-sm btn-outline-success" href={dashboardReplayUrl} target="_blank" rel="noreferrer">Replay</a> : null}
            <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => handleCopy(publicAuctionUrl, 'Public link')}>Copy public link</button>
            {dashboardWatchUrl ? <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => handleCopy(dashboardWatchUrl, 'Watch link')}>Copy watch link</button> : null}
            <button type="button" className="btn btn-sm btn-outline-dark" onClick={handleViewLogs}>View logs</button>
            {hasTakeoverAction ? <button type="button" className="btn btn-sm btn-warning" onClick={handleTakeover}>Take over control</button> : null}
          </div>
          <div className="small text-muted d-flex flex-wrap gap-3 align-items-center">
            <span>Status: {streamInfo?.isLive ? <span className="badge text-bg-danger">LIVE</span> : <span className="badge text-bg-secondary">OFFLINE</span>}</span>
            <span>Mode: {streamInfo?.streamingMode || auctionSettings?.streamingMode || '—'}</span>
            <span>Provider: {streamInfo?.provider || auctionSettings?.streamProvider || '—'}</span>
            <span>Health: {streamInfo?.liveHealth?.status || auctionSettings?.lastStreamHealth?.status || 'unknown'}</span>
          </div>
          {(streamInfo?.alerts || []).length > 0 ? (
            <div className="d-flex flex-wrap gap-1">
              {streamInfo.alerts.map((alert) => <span key={alert.code} className={`badge ${severityBadgeClass[alert.severity] || 'text-bg-secondary'}`}>{alert.message}</span>)}
            </div>
          ) : null}
        </div>
      </div>

      <div className="w-100">
        {activeTab === 'shuffle' && renderShuffleContent()}
        {activeTab === 'teams' && renderTeams()}
        {activeTab === 'players' && renderPlayers()}
      </div>

      {logModal.open && (
        <div className="modal d-block" tabIndex="-1" role="dialog" style={{ background: 'rgba(0,0,0,0.4)' }}>
          <div className="modal-dialog modal-lg modal-dialog-scrollable" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Online auction stream logs</h5>
                <button type="button" className="btn-close" aria-label="Close" onClick={() => setLogModal({ open: false, items: [] })} />
              </div>
              <div className="modal-body">
                {logModal.items.length === 0 && <div className="text-muted">No logs available.</div>}
                {logModal.items.map((event) => (
                  <div key={event._id} className="border-bottom pb-2 mb-2">
                    <div className="fw-semibold">{event.type}</div>
                    <div className="small">{event.message || '—'}</div>
                    <div className="small text-muted">{new Date(event.createdAt).toLocaleString()}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  
    </PageShell>
  );
};

export default StartOnlineAuctionPage;
